import React, { useState, useCallback } from "react"
import { useTheme } from "~/ui/styled"
import Animated, {
  useSharedValue,
  withSequence,
  withTiming,
  useAnimatedStyle,
  runOnJS
} from "~/ui/reanimated"
import { ColumnFlex, CenterFlex, Text, Button, Image } from "~components/El"
import { Avatar } from "~widgets/avatar"
import { useStores } from "~models"
import { useChatList } from "~hooks/useChatList"
import useGroupActions from "~hooks/useGroupActions"
import { globalToast } from "~utils";
import { AttachTransData } from "~proto"
import { PopupRoot, PopupImageBackground, PopupClose } from "../components/PopupShell"

const transferReceiveBg = require("~assets/images/chat/transfer_receive_bg.png")

interface Props {
  transferData: AttachTransData
  onOpen: () => void,
  onClose: () => void
}

const TransferReceiveNoticePopup: React.FC<Props> = ({ transferData, onOpen, onClose }) => {
  const theme = useTheme()
  const { walletStore, messageStore } = useStores()
  const { openTransferDetailNoticePopup } = useChatList()
  const { handleChatTransfer } = useGroupActions()
  const [loading, setLoading] = useState(false)

  const scale = useSharedValue(1)

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }))

  const onChatTransfer = useCallback(async () => {
    if (loading) return
    setLoading(true)
    handleChatTransfer({
        order_no: transferData?.order_no,
    }, (data) => {
        const next = data?.transfer_data
        messageStore.syncTransferMessageByDetail(next)
        messageStore.markBusinessMessageReadByDetail({ transferData: next })
        walletStore.getWalletInfo()
        setLoading(false)
        onOpen()
        openTransferDetailNoticePopup({ transferData: next })
    }, (error) => {
        globalToast.show(error.message || "领取失败")
    })
  }, [loading, onOpen])

  const handlePress = useCallback(() => {
    scale.value = withSequence(
      withTiming(0.85, { duration: 80 }),
      withTiming(1.15, { duration: 120 }),
      withTiming(1, { duration: 80 }, () => {
        runOnJS(onChatTransfer)()
      })
    )
  }, [onChatTransfer])

  return (
    <PopupRoot width="100%">
      <PopupImageBackground src={transferReceiveBg} resizeMode="cover">
        <ColumnFlex width="100%" height="100%" alignItems="center">
          <CenterFlex
            width="60px"
            height="60px"
            padding="2px"
            mt="50px"
            borderRadius="30px"
            bg={theme.colors.blockBackground}
          >
            <Avatar avatar={transferData?.sender_image} size={56} name={transferData?.sender_nickname} />
          </CenterFlex>

          <Text mt="8px" fontSize={18} color={theme.colors.textLight} fontWeight="600">
            {transferData?.sender_nickname || ""}向您转账
          </Text>
          <Text mt="4px" fontSize={14} color={theme.colors.textLight}>请确认收款</Text>
          <Text mt="8px" fontSize={14} color={theme.colors.textLight}>
            "{transferData?.remark || "恭喜发财，大吉大利"}"
          </Text>

          <Text mt="15px" fontSize={40} color={theme.colors.warning} fontWeight="600">
            ¥{transferData?.amount || "0"}
          </Text>
          <Text fontSize={12} color={theme.colors.textLight}>点击下方按钮确认收款</Text>

          <Animated.View style={[{ marginTop: 30 }, animatedStyle]}>
            <Button
              width="72px"
              height="72px"
              activeOpacity={1.0}
              disabled={loading}
              onClick={handlePress}
            >
              <Image
                width="100%"
                height="100%"
                src={require("~assets/images/chat/receive_btn.png")}
              />
            </Button>
          </Animated.View>
        </ColumnFlex>
      </PopupImageBackground>
      <PopupClose onClose={onClose} />
    </PopupRoot>
  )
}

export default TransferReceiveNoticePopup
